/**
 * Document arithmetic, shared verbatim by the browser and the server.
 *
 * ESM so Vite can bundle it into the client; the server reaches it with a
 * dynamic import from its async PDF path. The point is that there is exactly
 * one implementation: if the proposal page and the generated PDF computed
 * totals separately they could disagree, and the customer would receive two
 * documents quoting different prices for the same job.
 */

/**
 * Coerce to a usable number, never NaN.
 *
 * `Number('abc')` is NaN, and NaN propagates through every sum that touches
 * it — one bad value anywhere turns the whole total into NaN and the customer
 * receives a proposal quoting "$NaN". Treating unparseable input as zero is
 * visibly wrong on one line rather than silently wrong on the document.
 */
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Items have been through two shapes — a bare string, and {description, amount}.
 * Records in the earlier shape are still in the database, so every read path
 * has to tolerate both.
 */
export const normItem = (it) =>
  typeof it === 'string'
    ? { description: it, amount: 0 }
    : { description: it?.description || '', amount: num(it?.amount) };

export const normSection = (s) => ({
  name: s?.name || '',
  items: (s?.items || []).map(normItem),
});

export const normSections = (sections) => (sections || []).map(normSection);

/**
 * The hand-entered total to fall back on, for legacy proposals only.
 *
 * Only records whose items are all bare strings (from before line items had
 * amounts) qualify. A modern proposal whose amounts were cleared to zero must
 * total zero — falling back there made the old figure stick forever, since
 * every save wrote the fallback straight back as the new stored total.
 */
export function legacyTotal(proposal) {
  const items = (proposal?.sections || []).flatMap(s => s?.items || []);
  return items.every(it => typeof it === 'string') ? num(proposal?.total) : 0;
}

/**
 * Subtotal / tax / total for a proposal.
 *
 * `storedTotal` (see legacyTotal) is the fallback for older proposals that carry a hand-entered
 * total with no priced line items — those predate the section editor and must
 * still render the figure the customer was actually quoted.
 */
export function proposalTotals(sections, taxPct, storedTotal) {
  const subtotal = sections.reduce(
    (sum, sec) => sum + (sec.items || []).reduce((a, it) => a + num(it.amount), 0),
    0,
  );
  const pct = num(taxPct);
  const tax = subtotal * (pct / 100);
  const hasBreakdown = subtotal > 0;
  return {
    subtotal,
    tax,
    taxPct: pct,
    hasBreakdown,
    total: hasBreakdown ? subtotal + tax : num(storedTotal),
  };
}
